import React, { useEffect, useMemo, useRef, useState } from 'react';
import FusePageCarded from '@fuse/core/FusePageCarded';
import FuseAnimate from '@fuse/core/FuseAnimate';
import { Fab, Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { showErrorSwal, showSuccessSwal } from 'app/utility/Swalutility';
import RealtimeSession from '../services/RealtimeSession';

const BAR_COUNT = 24;

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		padding: theme.spacing(2)
	},
	stage: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(1.25),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`
	},
	statusRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(2),
		flexWrap: 'wrap'
	},
	statePill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(0.5, 1.5),
		borderRadius: 999,
		backgroundColor:
			theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
		fontSize: 12,
		fontWeight: 600,
		color: theme.palette.text.secondary,
		textTransform: 'uppercase',
		letterSpacing: 0.4
	},
	stateDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		backgroundColor: theme.palette.text.disabled,
		animation: '$dotPulse 1.6s ease-in-out infinite'
	},
	timer: {
		fontFamily:
			'"SFMono-Regular", "Menlo", "Monaco", "Consolas", "Liberation Mono", "Courier New", monospace',
		fontWeight: 700,
		color: theme.palette.text.primary
	},
	botStage: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%'
	},
	botVideo: {
		width: 150,
		height: 150,
		objectFit: 'contain',
		borderRadius: '50%',
		transition: 'transform 0.4s ease, filter 0.4s ease',
		filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.18))'
	},
	botVideoListening: {
		transform: 'scale(1.02)',
		filter: `drop-shadow(0 0 18px ${theme.palette.primary.main})`
	},
	botVideoSpeaking: {
		transform: 'scale(1.06)',
		filter: `drop-shadow(0 0 22px ${theme.palette.secondary.main})`,
		animation: '$botPulse 1.4s ease-in-out infinite'
	},
	botVideoProcessing: {
		filter: `drop-shadow(0 0 16px ${theme.palette.warning.main})`
	},
	visualizer: {
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		gap: 4,
		height: 56,
		width: '100%',
		maxWidth: 420
	},
	bar: {
		width: 6,
		borderRadius: 3,
		backgroundColor: theme.palette.text.disabled,
		opacity: 0.5,
		transformOrigin: 'bottom',
		animation: '$idlePulse 2.4s ease-in-out infinite'
	},
	barListening: {
		backgroundColor: theme.palette.primary.main,
		opacity: 1,
		animation: '$listeningWave 1.1s ease-in-out infinite'
	},
	barSpeaking: {
		backgroundColor: theme.palette.secondary.main,
		opacity: 1,
		animation: '$speakingWave 0.7s ease-in-out infinite'
	},
	barProcessing: {
		backgroundColor: theme.palette.warning.main,
		opacity: 0.85,
		animation: '$processingPulse 1s ease-in-out infinite'
	},
	micButton: {
		marginTop: 0
	},
	idleEmpty: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontSize: 12,
		textAlign: 'center'
	},
	idleHeading: {
		fontWeight: 600,
		color: theme.palette.text.primary,
		fontSize: 13
	},
	transcriptPanel: {
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5),
		maxHeight: 360,
		overflowY: 'auto'
	},
	transcriptHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	messageRow: {
		display: 'flex',
		gap: theme.spacing(1.5),
		alignItems: 'flex-start'
	},
	messageRowAssistant: {
		flexDirection: 'row'
	},
	messageRowUser: {
		flexDirection: 'row-reverse'
	},
	avatar: {
		width: 32,
		height: 32,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		color: theme.palette.common.white
	},
	avatarUser: { backgroundColor: theme.palette.primary.main },
	avatarAssistant: { backgroundColor: theme.palette.secondary.main },
	bubble: {
		padding: theme.spacing(1, 1.5),
		borderRadius: theme.spacing(1.5),
		maxWidth: '78%',
		fontSize: 14,
		lineHeight: 1.5,
		wordBreak: 'break-word',
		backgroundColor:
			theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
		color: theme.palette.text.primary
	},
	bubbleUser: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText
	},
	transcriptEmpty: {
		textAlign: 'center',
		color: theme.palette.text.secondary,
		padding: theme.spacing(3, 1)
	},
	'@keyframes dotPulse': {
		'0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
		'50%': { opacity: 1, transform: 'scale(1.25)' }
	},
	'@keyframes idlePulse': {
		'0%, 100%': { transform: 'scaleY(0.3)', opacity: 0.4 },
		'50%': { transform: 'scaleY(0.55)', opacity: 0.7 }
	},
	'@keyframes listeningWave': {
		'0%, 100%': { transform: 'scaleY(0.35)' },
		'50%': { transform: 'scaleY(1)' }
	},
	'@keyframes speakingWave': {
		'0%, 100%': { transform: 'scaleY(0.4)' },
		'50%': { transform: 'scaleY(1.05)' }
	},
	'@keyframes processingPulse': {
		'0%, 100%': { transform: 'scaleY(0.45)', opacity: 0.6 },
		'50%': { transform: 'scaleY(0.85)', opacity: 1 }
	},
	'@keyframes botPulse': {
		'0%, 100%': { transform: 'scale(1.04)' },
		'50%': { transform: 'scale(1.09)' }
	}
}));

function formatDuration(seconds) {
	const m = Math.floor(seconds / 60).toString().padStart(2, '0');
	const s = Math.floor(seconds % 60).toString().padStart(2, '0');
	return `${m}:${s}`;
}

function stateLabel(state) {
	switch (state) {
		case 'connecting':
			return 'Connecting';
		case 'listening':
			return 'Listening';
		case 'processing':
			return 'Processing';
		case 'speaking':
			return 'Speaking';
		case 'idle':
		default:
			return 'Idle';
	}
}

function stateDotColor(state, theme) {
	switch (state) {
		case 'listening':
			return theme.palette.primary.main;
		case 'speaking':
			return theme.palette.secondary.main;
		case 'processing':
			return theme.palette.warning.main;
		case 'connecting':
			return theme.palette.info.main;
		case 'idle':
		default:
			return theme.palette.text.disabled;
	}
}

function ConversationTab({ settings, messages, addMessage, clearMessages, addLead }) {
	const classes = useStyles();
	const theme = useTheme();

	const [convoState, setConvoState] = useState('idle');
	const [duration, setDuration] = useState(0);

	const sessionRef = useRef(null);
	const timerRef = useRef(null);
	const assistantAccRef = useRef('');

	const isActive = convoState !== 'idle';

	const startTimer = () => {
		if (timerRef.current) return;
		timerRef.current = setInterval(() => {
			setDuration(prev => prev + 1);
		}, 1000);
	};

	const stopTimer = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	};

	const handleDisconnect = () => {
		if (sessionRef.current) {
			sessionRef.current.disconnect();
			sessionRef.current = null;
		}
		stopTimer();
		setDuration(0);
		assistantAccRef.current = '';
		setConvoState('idle');
	};

	const handleConnect = async () => {
		if (!settings.apiKey) {
			showErrorSwal(
				'API Key Required',
				'Please add your OpenAI API key in Settings before starting a conversation.'
			);
			return;
		}

		setConvoState('connecting');
		assistantAccRef.current = '';

		const session = new RealtimeSession(
			settings.apiKey,
			settings.systemPrompt,
			settings.voice,
			{
				onConnectionChange: connected => {
					if (connected) {
						setConvoState('listening');
						setDuration(0);
						startTimer();
						showSuccessSwal('Connected', 'Voice session is live. Start speaking.');
					} else {
						stopTimer();
						setConvoState('idle');
						setDuration(0);
					}
				},
				onTranscriptUpdate: (role, text, isFinal) => {
					if (role === 'user') {
						if (isFinal && text && text.trim()) {
							addMessage({ role: 'user', content: text.trim() });
							setConvoState('processing');
						}
					} else if (role === 'assistant') {
						if (isFinal) {
							const finalText = (text && text.trim()) || assistantAccRef.current.trim();
							if (finalText) {
								addMessage({ role: 'assistant', content: finalText });
							}
							assistantAccRef.current = '';
						} else if (text) {
							assistantAccRef.current += text;
						}
					}
				},
				onAudioStart: () => {
					setConvoState('speaking');
				},
				onAudioEnd: () => {
					setConvoState(prev => (prev === 'idle' || prev === 'connecting' ? prev : 'listening'));
				},
				onInterrupted: () => {
					if (assistantAccRef.current.trim()) {
						addMessage({ role: 'assistant', content: assistantAccRef.current.trim() });
						assistantAccRef.current = '';
					}
					setConvoState(prev => (prev === 'idle' || prev === 'connecting' ? prev : 'listening'));
				},
				onLeadCaptured: leadData => {
					addLead(leadData);
					showSuccessSwal('Lead Captured', `${leadData.name || 'Unnamed'} added to leads.`);
				},
				onError: message => {
					showErrorSwal(
						'Voice Session Error',
						message || 'An error occurred with the voice session.'
					);
					handleDisconnect();
				}
			},
			{ interruptionsEnabled: settings.interruptionsEnabled !== false }
		);

		sessionRef.current = session;
		await session.connect();
	};

	const handleMicClick = () => {
		if (isActive) {
			handleDisconnect();
		} else {
			handleConnect();
		}
	};

	useEffect(() => {
		if (sessionRef.current) {
			sessionRef.current.updateVoice(settings.voice);
		}
	}, [settings.voice]);

	useEffect(() => {
		if (sessionRef.current) {
			sessionRef.current.updateSystemPrompt(settings.systemPrompt);
		}
	}, [settings.systemPrompt]);

	useEffect(() => {
		if (sessionRef.current) {
			sessionRef.current.updateInterruptions(settings.interruptionsEnabled !== false);
		}
	}, [settings.interruptionsEnabled]);

	useEffect(() => {
		return () => {
			stopTimer();
			if (sessionRef.current) {
				sessionRef.current.disconnect();
				sessionRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const bars = useMemo(() => {
		return Array.from({ length: BAR_COUNT }).map((_, i) => {
			const distance = Math.abs(i - (BAR_COUNT - 1) / 2);
			const baseHeight = 28 + (BAR_COUNT / 2 - distance) * 4;
			const delay = `${(i % 6) * 0.08}s`;
			return { height: Math.max(16, baseHeight), delay };
		});
	}, []);

	const barClass = (() => {
		switch (convoState) {
			case 'listening':
				return classes.barListening;
			case 'speaking':
				return classes.barSpeaking;
			case 'processing':
				return classes.barProcessing;
			default:
				return '';
		}
	})();

	return (
		<FusePageCarded
			header={
				<div className="flex flex-1 w-full items-center justify-between flex-wrap">
					<div className="hidden sm:flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<MicIcon fontSize="large" />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="flex mx-0 sm:mx-12" variant="h6">
								<span style={{ fontSize: '15px' }}>Voice Conversation</span>
							</Typography>
						</FuseAnimate>
					</div>
				</div>
			}
			content={
				<div className={classes.root}>
					<Paper className={classes.stage} elevation={0}>
						<div className={classes.statusRow}>
							<div className={classes.statePill}>
								<span
									className={classes.stateDot}
									style={{ backgroundColor: stateDotColor(convoState, theme) }}
								/>
								{stateLabel(convoState)}
							</div>
							<Typography variant="h6" className={classes.timer}>
								{formatDuration(duration)}
							</Typography>
						</div>

						<div className={classes.botStage}>
							<video
								className={`${classes.botVideo} ${
									convoState === 'speaking'
										? classes.botVideoSpeaking
										: convoState === 'listening'
										? classes.botVideoListening
										: convoState === 'processing'
										? classes.botVideoProcessing
										: ''
								}`}
								src="/assets/images/aiCall/Lumina-Bot.webm"
								autoPlay
								loop
								muted
								playsInline
							/>
						</div>

						<div className={classes.visualizer}>
							{bars.map((bar, idx) => (
								<div
									key={idx}
									className={`${classes.bar} ${barClass}`}
									style={{
										height: bar.height,
										animationDelay: bar.delay
									}}
								/>
							))}
						</div>

						<Fab
							color={isActive ? 'secondary' : 'primary'}
							onClick={handleMicClick}
							className={classes.micButton}
							aria-label={isActive ? 'Stop conversation' : 'Start conversation'}
							disabled={convoState === 'connecting'}
						>
							{isActive ? <StopIcon /> : <MicIcon />}
						</Fab>

						{convoState === 'idle' && (
							<div className={classes.idleEmpty}>
								<span className={classes.idleHeading}>Ready to Converse —</span>
								<span>tap the microphone and start speaking.</span>
							</div>
						)}
					</Paper>

					<Paper className={classes.transcriptPanel} elevation={0}>
						<div className={classes.transcriptHeader}>
							<Typography variant="subtitle1" style={{ fontWeight: 600 }}>
								Transcript
							</Typography>
							{messages.length > 0 && (
								<Typography
									variant="caption"
									style={{ cursor: 'pointer', color: theme.palette.primary.main }}
									onClick={clearMessages}
								>
									Clear
								</Typography>
							)}
						</div>

						{messages.length === 0 ? (
							<div className={classes.transcriptEmpty}>
								<Typography variant="body2">
									Your conversation transcript will appear here.
								</Typography>
							</div>
						) : (
							messages.map(msg => {
								const isUser = msg.role === 'user';
								return (
									<div
										key={msg.id}
										className={`${classes.messageRow} ${
											isUser ? classes.messageRowUser : classes.messageRowAssistant
										}`}
									>
										<div
											className={`${classes.avatar} ${
												isUser ? classes.avatarUser : classes.avatarAssistant
											}`}
										>
											{isUser ? (
												<PersonIcon fontSize="small" />
											) : (
												<SmartToyIcon fontSize="small" />
											)}
										</div>
										<div
											className={`${classes.bubble} ${isUser ? classes.bubbleUser : ''}`}
										>
											{msg.content}
										</div>
									</div>
								);
							})
						)}
					</Paper>

				</div>
			}
		/>
	);
}

export default ConversationTab;
